import React, { useState, useContext, useEffect, useCallback, useRef } from 'react';
import { CurpContext } from '../context/CurpContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from '../components/Logo';
import BackgroundLayout from '../components/BackgroundLayout';

import EmailSuggestions from '../components/EmailSuggestions';
import BirthDateInput from '../components/BirthDateInput';
import CurpBuilder from '../components/CurpBuilder';
import { toast } from 'react-hot-toast';

const FormPage = () => {
    const location = useLocation();
    const isForeignInitial = location.state?.isForeign || false;
    const navigate = useNavigate();

    const inputClass = "border p-2 rounded-xl w-full drop-shadow-sm mt-1 bg-white/50 focus:bg-white focus:ring-2 focus:ring-[#f8485e]/20 outline-none transition-all text-gray-700 font-medium";
    const labelClass = "block text-gray-600 font-bold uppercase text-[11px] tracking-wider mb-1 ml-1";
    const [burstKey, setBurstKey] = useState(null);
    const { curp } = useContext(CurpContext);
    const topRef = useRef(null);
    
    // WIZARD STATE
    const [currentStep, setCurrentStep] = useState(0);
    const totalSteps = 4;
    const [direction, setDirection] = useState(1);

    const [formData, setFormData] = useState({
        name: '',
        surnames: '',
        nickname: '',
        foreign: isForeignInitial,
        privacy: false,
        curp: isForeignInitial ? '' : curp,
        email: '',
        lives_outside_mexico: false,
        postal_code: '',
        state: '',
        city: '',
        neighborhood: '',
        street: '',
        date_birth: '',
        schooling: '',
        gender_identity: '',
        disability: false,
        health_status: '',
        sexual_orientation: '',
        ethnoracial_identity: '',
        intersex_status: false,
        phone_number: '',
    });

    // LOAD DRAFT
    useEffect(() => {
        const saved = localStorage.getItem('formDraft');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setFormData(prev => ({ 
                    ...prev, 
                    ...parsed, 
                    curp: parsed.foreign ? parsed.curp : prev.curp,
                    foreign: parsed.foreign !== undefined ? parsed.foreign : prev.foreign
                }));
            } catch (e) {
                console.error("Failed to parse draft");
            }
        }
    }, []);

    // SAVE DRAFT
    useEffect(() => {
        localStorage.setItem('formDraft', JSON.stringify(formData));
    }, [formData]);

    useEffect(() => {
        if (!formData.foreign) {
            setFormData((prevData) => ({ ...prevData, curp }));
        }
    }, [curp, formData.foreign]);

    const estadosMexico = [
        { code: 'AGS', name: 'Aguascalientes' }, { code: 'BC', name: 'Baja California' }, { code: 'BCS', name: 'Baja California Sur' },
        { code: 'CAMP', name: 'Campeche' }, { code: 'CHIS', name: 'Chiapas' }, { code: 'CHIH', name: 'Chihuahua' },
        { code: 'CDMX', name: 'Ciudad de México' }, { code: 'COAH', name: 'Coahuila' }, { code: 'COL', name: 'Colima' },
        { code: 'DGO', name: 'Durango' }, { code: 'GTO', name: 'Guanajuato' }, { code: 'GRO', name: 'Guerrero' },
        { code: 'HGO', name: 'Hidalgo' }, { code: 'JAL', name: 'Jalisco' }, { code: 'MEX', name: 'Estado de México' },
        { code: 'MICH', name: 'Michoacán' }, { code: 'MOR', name: 'Morelos' }, { code: 'NAY', name: 'Nayarit' },
        { code: 'NL', name: 'Nuevo León' }, { code: 'OAX', name: 'Oaxaca' }, { code: 'PUE', name: 'Puebla' },
        { code: 'QRO', name: 'Querétaro' }, { code: 'QROO', name: 'Quintana Roo' }, { code: 'SLP', name: 'San Luis Potosí' },
        { code: 'SIN', name: 'Sinaloa' }, { code: 'SON', name: 'Sonora' }, { code: 'TAB', name: 'Tabasco' },
        { code: 'TAMPS', name: 'Tamaulipas' }, { code: 'TLAX', name: 'Tlaxcala' }, { code: 'VER', name: 'Veracruz' },
        { code: 'YUC', name: 'Yucatán' }, { code: 'ZAC', name: 'Zacatecas' },
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const capitalizeNames = (str) => {
            return str.toLowerCase().split(/\s+/)
                .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ');
        };
        let newValue;
        if (type === 'checkbox') {
            newValue = checked;
        } else if (name === 'name' || name === 'surnames' || name === 'nickname') {
            newValue = capitalizeNames(value);
        } else {
            newValue = value;
        }

        setFormData({
            ...formData,
            [name]: newValue
        });
    };

    const handleEmailSelect = (fullEmail) => {
        setFormData({ ...formData, email: fullEmail });
    };

    const handleGeneratedCurp = useCallback((generatedCurp) => {
        setFormData((prev) => ({ ...prev, curp: generatedCurp }));
    }, []);

    // WIZARD VALIDATION
    const isStepValid = (stepIndex) => {
        switch (stepIndex) {
            case 0:
                let valid0 = !!formData.name?.trim() && !!formData.surnames?.trim() && !!String(formData.phone_number || '').trim() && !!formData.nickname?.trim() && !!formData.email?.trim();
                if (!formData.foreign) valid0 = valid0 && !!formData.curp?.trim();
                return valid0;
            case 1:
                if (formData.lives_outside_mexico) return true;
                return !!String(formData.postal_code || '').trim() && !!formData.state?.trim() && !!formData.city?.trim() && !!formData.neighborhood?.trim() && !!formData.street?.trim();
            case 2:
                return formData.sexual_orientation !== '' && !!formData.date_birth?.trim() && formData.schooling !== '' && formData.gender_identity !== '' && formData.ethnoracial_identity !== '';
            case 3:
                return formData.privacy === true;
            default:
                return false;
        }
    };

    const scrollToNextField = (nextFieldId) => {
        if (!nextFieldId) return;
        setTimeout(() => {
            const el = document.getElementById(nextFieldId);
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 300);
    };

    // Auto-scroll to top when step changes
    useEffect(() => {
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'instant', block: 'start' });
        }
    }, [currentStep]);

    const nextStep = () => {
        if (isStepValid(currentStep)) {
            setDirection(1);
            setCurrentStep((prev) => prev + 1);
        }
    };

    const prevStep = () => {
        setDirection(-1);
        setCurrentStep((prev) => prev - 1);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isStepValid(3)) return;

        try {
            const response = await fetch('https://admin-django-inspira.azurewebsites.net/api/users/register/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                let errorData;
                try { errorData = await response.json(); } catch (jsonError) { throw new Error('Error inesperado del servidor'); }
                if (errorData && typeof errorData === 'object') {
                    const firstErrorKey = Object.keys(errorData)[0];
                    const firstErrorMessage = errorData[firstErrorKey]?.[0] || 'Ocurrió un error';
                    toast.error(`${firstErrorKey}: ${firstErrorMessage}`);
                } else {
                    toast.error('Ocurrió un error en el registro');
                }
                return;
            }

            localStorage.removeItem('formDraft');
            toast.success('¡Yuju, registro exitoso!');
            navigate('/vulnerability');

        } catch (error) {
            console.error('Error:', error);
            toast.error(error.message || 'Algo salió mal. Intenta más tarde.');
        }
    };

    const variants = {
        enter: (direction) => ({
            x: direction > 0 ? 1000 : -1000,
            opacity: 0
        }),
        center: {
            zIndex: 1,
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            zIndex: 0,
            x: direction < 0 ? 1000 : -1000,
            opacity: 0
        })
    };

    const renderStep0 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-center text-7xl font-bold text-[#f8485e] mt-2 mb-2 drop-shadow-sm'>😎</motion.h1>
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-4xl lg:text-5xl font-bold text-[#f8485e] mt-2 drop-shadow-md uppercase text-center mb-6'>Vamos a conocernos</motion.h1>
            
            <div id="field-name" className="flex space-x-4 mb-5">
                <div className='w-1/2'>
                    <label className={labelClass}>Nombres</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} className={inputClass} required />
                </div>
                <div className='w-1/2'>
                    <label className={labelClass}>Apellidos</label>
                    <input type="text" name="surnames" value={formData.surnames} onChange={handleChange} className={inputClass} required />
                </div>
            </div>
            <div id="field-phone" className='flex space-x-4 mb-5'>
                <div className='w-full'>
                    <label className={labelClass}>Número telefónico</label>
                    <input type="number" name="phone_number" value={formData.phone_number} onChange={handleChange} required className={inputClass} />
                </div>
            </div>
            
            <div className='flex justify-center mb-6'>
                <motion.button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, foreign: !prev.foreign }))}
                    whileTap={{ scale: 0.95 }}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-200 shadow-sm border-2 ${
                        formData.foreign ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.35)]' : 'bg-white border-gray-200 text-gray-500 hover:border-[#f8485e]/50'
                    }`}
                >
                    🌍 Soy extranjero
                </motion.button>
            </div>

            {formData.foreign && <CurpBuilder formData={formData} onCurpChange={handleGeneratedCurp} />}

            <div className='w-full text-left mt-5 bg-white/40 backdrop-blur-md rounded-[32px] p-8 border border-[#f8485e]/5 shadow-xl mb-8'>
                <h1 className="text-[12px] font-semibold mt-0 mb-4 text-justify text-gray-700 leading-relaxed uppercase tracking-tight">En Inspira, queremos que te sientas cómodx y respetadx. Elige el nombre con el que deseas ser conocidx. 😇</h1>
                <div className="mb-2">
                    <label className={labelClass}>Nombre elegido ✨</label>
                    <input type="text" name="nickname" value={formData.nickname} onChange={handleChange} className={inputClass} required />
                </div>
            </div>

            {!formData.foreign && (
                <div id="field-curp" className="mb-5">
                    <label className={labelClass}>CURP</label>
                    <input type="text" name="curp" value={formData.curp} onChange={handleChange} className={inputClass} required />
                </div>
            )}
            <div id="field-email" className="mb-8">
                <label className={labelClass}>Correo</label>
                <input type="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} required />
                <EmailSuggestions email={formData.email} onSelect={handleEmailSelect} />
            </div>
        </div>
    );

    const renderStep1 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-center text-7xl font-bold text-[#f8485e] mt-2 mb-2 drop-shadow-sm leading-none'>🏠</motion.h1>
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-4xl font-bold text-[#f8485e] mt-2 drop-shadow-md uppercase text-center mb-8'>Tu Ubicación</motion.h1>
            
            <div className="mb-8 flex justify-center">
                <motion.button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, lives_outside_mexico: !prev.lives_outside_mexico }))}
                    whileTap={{ scale: 0.95 }}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm uppercase tracking-wider transition-all duration-200 shadow-sm border-2 ${
                        formData.lives_outside_mexico ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.35)]' : 'bg-white border-gray-200 text-gray-500 hover:border-[#f8485e]/50'
                    }`}
                >
                    🏠 Vivo fuera de México
                </motion.button>
            </div>
            
            {!formData.lives_outside_mexico && (
                <>
                    <div id="field-postal" className='flex space-x-4 mb-5'>
                        <div className="w-1/2">
                            <label className={labelClass}>Código Postal</label>
                            <input type="number" name="postal_code" value={formData.postal_code} onChange={handleChange} className={inputClass} required={!formData.lives_outside_mexico} />
                        </div>
                        <div className="w-1/2">
                            <label className={labelClass}>Estado</label>
                            <select name="state" value={formData.state} onChange={handleChange} className={inputClass} required={!formData.lives_outside_mexico} >
                                <option value="">Selecciona</option>
                                {estadosMexico.map((estado) => (
                                    <option key={estado.code} value={estado.code}>{estado.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div id="field-city" className='flex space-x-4 mb-5'>
                        <div className="w-1/2">
                            <label className={labelClass}>Ciudad / Alcaldía</label>
                            <input type="text" name="city" value={formData.city} onChange={handleChange} className={inputClass} required={!formData.lives_outside_mexico} />
                        </div>
                        <div className="w-1/2">
                            <label className={labelClass}>Colonia</label>
                            <input type="text" name="neighborhood" value={formData.neighborhood} onChange={handleChange} className={inputClass} required={!formData.lives_outside_mexico} />
                        </div>
                    </div>
                    <div id="field-street" className="mb-8">
                        <label className={labelClass}>Calle y número</label>
                        <input type="text" name="street" value={formData.street} onChange={handleChange} className={inputClass} required={!formData.lives_outside_mexico} />
                    </div>
                </>
            )}
        </div>
    );

    const renderStep2 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-center text-7xl font-bold text-[#f8485e] mt-2 mb-2 drop-shadow-sm leading-none'>🌚</motion.h1>
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-4xl font-bold text-[#f8485e] mt-2 drop-shadow-md uppercase text-center mb-8'>Identidad</motion.h1>

            <div id="field-date-birth" className="mb-8">
                <label className={labelClass}>Fecha de Nacimiento</label>
                <BirthDateInput value={formData.date_birth} onChange={(val) => setFormData({ ...formData, date_birth: val })} required />
            </div>

            <div id="field-sexual-orientation" className="mb-6">
                <label className={labelClass}>Orientación Sexual</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                        { value: '0', label: 'Gay' }, { value: '1', label: 'Lesbiana' }, { value: '2', label: 'Bisexual' },
                        { value: '3', label: 'Heterosexual' }, { value: '4', label: 'Asexual' }, { value: '5', label: 'Demisexual' }, { value: '6', label: 'Pansexual' },
                    ].map((opt) => (
                        <div key={opt.value} className="relative">
                            <motion.button type="button" whileTap={{ scale: 0.92 }}
                                onClick={() => { setFormData(prev => ({ ...prev, sexual_orientation: opt.value })); setBurstKey(`${opt.value}-${Date.now()}`); scrollToNextField('field-gender'); }}
                                className={`relative w-full py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 overflow-hidden ${
                                    formData.sexual_orientation === opt.value ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                                }`}
                            >{opt.label}</motion.button>
                            <AnimatePresence>
                                {burstKey?.startsWith(opt.value) && (
                                    <motion.span key={burstKey} className="pointer-events-none absolute rounded-xl" style={{ inset: 0, border: '1px solid #f8485e', boxShadow: '0 0 6px 1px rgba(248,72,94,0.2)' }}
                                        initial={{ opacity: 0.5, scale: 1 }} animate={{ opacity: 0, scale: 1.25 }} exit={{ opacity: 0 }} transition={{ duration: 0.9, ease: 'easeOut' }} />
                                )}
                            </AnimatePresence>
                        </div>
                    ))}
                </div>
            </div>

            <div id="field-gender" className="mb-8">
                <label className={labelClass}>Género</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                        { value: '0', label: 'Mujer trans' }, { value: '1', label: 'Hombre trans' }, { value: '2', label: 'No binaria' },
                        { value: '3', label: 'Mujer' }, { value: '4', label: 'Hombre' },
                    ].map((opt) => (
                        <div key={opt.value} className="relative">
                            <motion.button type="button" whileTap={{ scale: 0.92 }}
                                onClick={() => { setFormData(prev => ({ ...prev, gender_identity: opt.value })); setBurstKey(`gen-${opt.value}-${Date.now()}`); scrollToNextField('field-ethnoracial'); }}
                                className={`relative w-full py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                                    formData.gender_identity === opt.value ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                                }`}
                            >{opt.label}</motion.button>
                        </div>
                    ))}
                </div>
            </div>

            <div id="field-ethnoracial" className="mb-8">
                <label className={labelClass}>Identidad étnico-racial</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                        { value: '0', label: 'Afromexicano(a)' }, { value: '1', label: 'Pueblo originario' }, { value: '2', label: 'Mestizo(a)' },
                        { value: '3', label: 'Asiático(a)' }, { value: '4', label: 'Árabe' }, { value: '5', label: 'Eurodescendiente' }, { value: '6', label: 'Otra' },
                    ].map((opt) => (
                        <div key={opt.value} className="relative">
                            <motion.button type="button" whileTap={{ scale: 0.92 }}
                                onClick={() => { setFormData(prev => ({ ...prev, ethnoracial_identity: opt.value })); scrollToNextField('field-schooling'); }}
                                className={`relative w-full py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                                    formData.ethnoracial_identity === opt.value ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                                }`}
                            >{opt.label}</motion.button>
                        </div>
                    ))}
                </div>
                <p className="text-[10px] my-2 text-[#f8485e] font-bold px-1 leading-tight">(Puedes elegir la que mejor represente tu identidad. Si eres "mexicano(a)", sugerimos "mestizo(a)".)</p>
            </div>

            <div id="field-schooling" className="mb-6">
                <label className={labelClass}>Escolaridad</label>
                <div className="grid grid-cols-2 gap-2 mt-1">
                    {[
                        { value: '0', label: 'Sin estudios' }, { value: '1', label: 'Primaria' }, { value: '2', label: 'Secundaria' },
                        { value: '3', label: 'Preparatoria' }, { value: '4', label: 'Carrera técnica' }, { value: '5', label: 'Licenciatura' },
                        { value: '6', label: 'Maestría' }, { value: '7', label: 'Doctorado' },
                    ].map((opt) => (
                        <div key={opt.value} className="relative">
                            <motion.button type="button" whileTap={{ scale: 0.92 }}
                                onClick={() => { setFormData(prev => ({ ...prev, schooling: opt.value })); }}
                                className={`relative w-full py-4 px-3 rounded-xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                                    formData.schooling === opt.value ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                                }`}
                            >{opt.label}</motion.button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="w-full">
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-center text-7xl font-bold text-[#f8485e] mt-2 mb-2 drop-shadow-sm leading-none'>🩺</motion.h1>
            <motion.h1 initial={{ opacity: 0 }} animate={{ opacity: 1 }} className='text-4xl font-bold text-[#f8485e] mt-2 drop-shadow-md uppercase text-center mb-8'>Salud y Privacidad</motion.h1>

            <div className='flex gap-3 mb-8 mt-4'>
                <motion.button type="button" onClick={() => setFormData(prev => ({ ...prev, disability: !prev.disability }))} whileTap={{ scale: 0.95 }}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                        formData.disability ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                    }`}>♿ Tengo una discapacidad</motion.button>
                <motion.button type="button" onClick={() => setFormData(prev => ({ ...prev, intersex_status: !prev.intersex_status }))} whileTap={{ scale: 0.95 }}
                    className={`flex-1 flex items-center justify-center gap-2 py-4 rounded-2xl font-bold text-xs uppercase tracking-widest transition-all duration-200 border-2 ${
                        formData.intersex_status ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_4px_14px_rgba(248,72,94,0.3)]' : 'bg-white border-gray-200 text-gray-400 hover:border-[#f8485e]/40'
                    }`}>⚧ Soy intersexual</motion.button>
            </div>

            <div id="field-privacy" className="w-full mb-10">
                <motion.button type="button" onClick={() => setFormData(prev => ({ ...prev, privacy: !prev.privacy }))} whileTap={{ scale: 0.97 }}
                    className={`w-full flex items-center gap-4 p-5 rounded-[24px] border-2 transition-all duration-200 ${
                        formData.privacy ? 'bg-[#f8485e] border-[#f8485e] text-white shadow-[0_8px_24px_rgba(248,72,94,0.3)]' : 'bg-white/40 backdrop-blur-md border-gray-200 text-gray-600 hover:border-[#f8485e]/40'
                    }`}
                >
                    <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                        formData.privacy ? 'bg-white border-white' : 'border-gray-300'
                    }`}>
                        {formData.privacy && (
                            <svg viewBox="0 0 12 12" className="w-3.5 h-3.5" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="#f8485e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        )}
                    </span>
                    <span className="text-left">
                        <span className="block text-[11px] font-black uppercase tracking-widest mb-0.5">Aviso de privacidad</span>
                        <span className={`text-[11px] font-medium ${ formData.privacy ? 'text-white/80' : 'text-gray-400' }`}>
                            He leído y acepto el{' '}
                            <a href="https://drive.google.com/file/d/1NPJOdGlt8zEKC2SyiZ9sP3B5g_pm7nss/view" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                                className={`underline font-bold ${ formData.privacy ? 'text-white' : 'text-[#f8485e]' } hover:opacity-80 transition-opacity`}>Aviso de Privacidad</a>
                        </span>
                    </span>
                </motion.button>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 0: return renderStep0();
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            default: return null;
        }
    };

    return (
        <BackgroundLayout className="px-8 flex flex-col items-center pb-24">
            <div ref={topRef} className="absolute top-0 w-full h-1" />
            <Logo />

            <div className="w-full lg:w-2/3 mt-6 mb-4">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest text-center mb-2">Paso {currentStep + 1} de {totalSteps}</p>
                <div className="w-full h-1.5 bg-white/50 rounded-full overflow-hidden">
                    <motion.div 
                        className="h-full bg-[#f8485e]"
                        initial={{ width: 0 }}
                        animate={{ width: `${((currentStep + 1) / totalSteps) * 100}%` }}
                        transition={{ duration: 0.3 }}
                    />
                </div>
            </div>

            {currentStep === 0 && (
                <div className="w-full lg:w-2/3 mb-4">
                    <p className="text-xs text-center font-bold text-gray-500 uppercase tracking-widest">⚡ Tiempo estimado: 3 minutos</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="w-full lg:w-2/3 overflow-hidden mt-4">
                <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                        key={currentStep}
                        custom={direction}
                        variants={variants}
                        initial="enter"
                        animate="center"
                        exit="exit"
                        transition={{
                            x: { type: "spring", stiffness: 300, damping: 30 },
                            opacity: { duration: 0.2 }
                        }}
                    >
                        {renderCurrentStep()}
                    </motion.div>
                </AnimatePresence>

                <div className="flex justify-between gap-4 mt-8 mb-12">
                    <button
                        type="button"
                        onClick={prevStep}
                        className={`flex-1 py-4 rounded-2xl font-black uppercase tracking-widest transition-all ${
                            currentStep === 0 ? 'opacity-0 pointer-events-none' : 'bg-white border-2 border-gray-200 text-gray-500 hover:border-[#f8485e]/40'
                        }`}
                    >
                        Atrás
                    </button>
                    
                    {currentStep < totalSteps - 1 ? (
                        <button
                            type="button"
                            onClick={nextStep}
                            disabled={!isStepValid(currentStep)}
                            className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                isStepValid(currentStep)
                                    ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                            }`}
                        >
                            Siguiente ✨
                        </button>
                    ) : (
                        <button
                            type="submit"
                            disabled={!isStepValid(currentStep)}
                            className={`flex-1 py-4 rounded-2xl shadow-xl font-black uppercase tracking-widest transition-all active:scale-95 ${
                                isStepValid(currentStep)
                                    ? 'bg-[#f8485e] hover:bg-[#d93a4f] text-white'
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none'
                            }`}
                        >
                            Enviar 🚀
                        </button>
                    )}
                </div>
            </form>
        </BackgroundLayout>
    );
};

export default FormPage;
